import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { ArrowTopRightOnSquareIcon, ArrowUturnLeftIcon, CheckIcon, MegaphoneIcon } from '@heroicons/react/24/outline'
import usePlacowkaKampanieData from './hooks/usePlacowkaKampanieData'
import usePlacowkaKampanieActions from './hooks/usePlacowkaKampanieActions'

const STATUS_OPTIONS = [
	{ value: '', label: 'Wszystkie' },
	{ value: 'done', label: 'Wykonane' },
	{ value: 'undone', label: 'Niewykonane' },
]

const formatDateTime = value => {
	if (!value) return '-'
	return moment(value).isValid() ? moment(value).format('YYYY-MM-DD HH:mm') : '-'
}

const getKampaniaId = item => {
	if (!item?.kampaniaId) return null
	if (typeof item.kampaniaId === 'object') return item.kampaniaId?._id || item.kampaniaId?.id || null
	return item.kampaniaId
}

const getKampaniaName = item => item?.kampaniaName || '-'

export default function PlacowkaKampanieCard({ placowkaId }) {
	const [selectedStatus, setSelectedStatus] = useState('')
	const { data, loading, error, loadData } = usePlacowkaKampanieData(placowkaId)
	const { processingId, handleMarkAsDone, handleMarkAsUndone } = usePlacowkaKampanieActions(loadData)

	useEffect(() => {
		loadData()
	}, [loadData])

	const filteredRows = useMemo(() => {
		const rows = Array.isArray(data) ? data : []
		if (!selectedStatus) return rows
		if (selectedStatus === 'done') return rows.filter(item => !!item?.doneDate)
		return rows.filter(item => !item?.doneDate)
	}, [data, selectedStatus])

	return (
		<li className="col-span-1">
			<div className="flex h-[440px] w-full flex-col overflow-hidden rounded-md border border-slate-200 bg-gradient-to-b from-slate-50 to-white shadow-sm">
				<div className="flex flex-shrink-0 flex-row gap-3 border-b border-slate-300 bg-gray-100 px-6 py-3 sm:items-center sm:justify-between">
					<div className="flex items-start gap-3">
						<div className="flex flex-col gap-1">
							<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
								<MegaphoneIcon className="h-5 w-5 text-sky-700" />
								<span>Kampanie</span>
							</h3>
						</div>
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-3">
						<label className="flex items-center gap-2 text-sm text-slate-700">
							<span className="font-medium">Status</span>
							<select
								value={selectedStatus}
								onChange={event => setSelectedStatus(event.target.value)}
								className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
							>
								{STATUS_OPTIONS.map(option => (
									<option key={option.value || 'all'} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</label>
						<Link
							to="/komunikacja/kampanie"
							className="inline-flex items-center gap-2 text-sm text-sky-700 hover:text-sky-800"
						>
							<ArrowTopRightOnSquareIcon className="h-5 w-5" />
							<span>Przejdz do listy</span>
						</Link>
					</div>
				</div>

				<div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 sm:px-6 sm:pb-6">
					{loading ? (
						<div className="flex flex-1 items-center justify-center gap-3 rounded-md bg-white px-4 text-sm text-slate-600">
							<MegaphoneIcon className="h-5 w-5 text-slate-400" />
							<span>Ladowanie kampanii...</span>
						</div>
					) : error ? (
						<div className="flex flex-1 items-center justify-center gap-3 rounded-md bg-white px-4 text-sm text-red-600">
							<span>{error}</span>
						</div>
					) : data.length === 0 ? (
						<div className="flex flex-1 items-center justify-center gap-3 rounded-md bg-white px-4 text-sm text-slate-600">
							<MegaphoneIcon className="h-5 w-5 text-slate-400" />
							<span>Brak kampanii dla tej placowki.</span>
						</div>
					) : filteredRows.length === 0 ? (
						<div className="flex flex-1 items-center justify-center gap-3 rounded-md bg-white px-4 text-sm text-slate-600">
							<MegaphoneIcon className="h-5 w-5 text-slate-400" />
							<span>Brak kampanii dla wybranego statusu.</span>
						</div>
					) : (
						<div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
							<div className="min-h-0 flex-1 overflow-auto">
								<table className="min-w-full text-sm text-slate-800">
									<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
										<tr>
											<th className="px-4 py-3 text-left">Kampania</th>
											<th className="px-4 py-3 text-left">Status</th>
											<th className="px-4 py-3 text-left">Data wykonania</th>
											<th className="px-4 py-3 text-right">Akcje</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
										{filteredRows.map((item, index) => {
											const rowKey = item?._id || index
											const kampaniaId = getKampaniaId(item)
											const kampaniaName = getKampaniaName(item)
											const isDone = !!item?.doneDate
											const isProcessing = processingId === item?._id

											return (
												<tr key={rowKey} className="bg-white transition-colors hover:bg-slate-50/80">
													<td className="px-4 py-3 text-sm font-semibold text-slate-900">
														{kampaniaId ? (
															<Link
																to={`/komunikacja/kampanie/${kampaniaId}/placowki`}
																className="text-sky-700 hover:text-sky-800 hover:underline"
															>
																{kampaniaName}
															</Link>
														) : (
															kampaniaName
														)}
													</td>
													<td className="whitespace-nowrap px-4 py-3 text-sm text-slate-800">
														{isDone ? (
															<span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
																Wykonane
															</span>
														) : (
															<span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
																Niewykonane
															</span>
														)}
													</td>
													<td className="whitespace-nowrap px-4 py-3 text-sm text-slate-800">
														{formatDateTime(item?.doneDate)}
													</td>
													<td className="whitespace-nowrap px-4 py-3 text-right text-sm text-slate-800">
														{!isDone ? (
															<button
																type="button"
																onClick={() => handleMarkAsDone(item)}
																disabled={isProcessing}
																className="inline-flex items-center gap-1 rounded-md border border-green-100 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60"
															>
																<CheckIcon className="h-4 w-4" />
																<span>Wykonane</span>
															</button>
														) : (
															<button
																type="button"
																onClick={() => handleMarkAsUndone(item)}
																disabled={isProcessing}
																className="inline-flex items-center gap-1 rounded-md border border-amber-100 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
															>
																<ArrowUturnLeftIcon className="h-4 w-4" />
																<span>Niewykonane</span>
															</button>
														)}
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</div>
			</div>
		</li>
	)
}
